import React, { CSSProperties, Ref, useImperativeHandle, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import classNames from 'classnames';
import './styles.scss';
import AcIcon from 'ac-components/ac-icon';

let seed = 0;

function getUid() {
	return `alert_${Date.now()}_${seed++}`;
}
type BaseProps = {
	className?: string;
	style?: CSSProperties;
	type: 'success' | 'warn' | 'error';
	closable?: boolean; // 可否点击关闭
	duration?: number; // 自动延迟关闭时间
	onClose?(): void; // 是否点击关闭
	onClick?(e: React.MouseEvent<HTMLDivElement>): void;
	closeIcon?: React.ReactNode;
}
type AlertProps = React.PropsWithChildren<BaseProps>;

const Icons = {
	success: 'a-icon_progressdone',
	warn: 'icon_warning',
	error: 'icon_award',
}
class BaseAlert extends React.PureComponent<AlertProps> {
	timer: number | null;
	close = (e?: React.MouseEvent<HTMLDivElement>) => {
		if (e) {
			e.stopPropagation();
		}
		this.clearCloseTimer();
		if (this.props.onClose) {
			this.props.onClose();
		}
	}
	startCloseTimer = () => {
		const { duration = 2 } = this.props;
		if (duration) {
			this.timer = window.setTimeout(() => {
				this.close();
			}, duration * 1000);
		}
	}
	clearCloseTimer = () => {
		if (this.timer) {
			clearTimeout(this.timer);
			this.timer = null;
		}
	}
	restartCloseTimer = () => {
		this.clearCloseTimer();
		this.startCloseTimer();
	}
	componentDidMount() {
		this.startCloseTimer();
	}
	componentDidUpdate(prevProps: AlertProps) {
		if (this.props.duration !== prevProps.duration) {
			this.restartCloseTimer();
		}
	}
	componentWillUnmount() {
		this.clearCloseTimer()
	}
	render() {
		const { className, type, closable, style, onClose, onClick, closeIcon, children } = this.props;
		return (
			<div className={classNames(className, 'alert', type, { closable })} style={style} onClick={onClick}>
				<AcIcon icon={Icons[type]} type={type} />
				{children}
				{!closable && (
					<a onClick={onClose}>
						{closeIcon || <AcIcon icon={'icon_close'} />}
					</a>
				)}
			</div>
		)
	}
}

const Alert = (props: AlertProps) => {
	return <BaseAlert {...props} />
}

const getContainer = (function getContainer() {
	let container: HTMLElement;
	return (wrap?: HTMLElement) => {
		if (wrap) return wrap;
		if (!container) {
			container = document.createElement('div');
			container.className = 'alert-container';
			document.body.appendChild(container);
		}
		return container;
	}
})();

Alert.Portal = (props: AlertProps) => {
	const container = getContainer().appendChild(document.createElement('div'));
	const onCloseHandle = () => {
		if (typeof props.onClose === 'function') {
			props.onClose();
		}
		const parentElement = container.parentElement;
		if (parentElement) {
			parentElement.removeChild(container);
		}
	}
	return ReactDOM.createPortal(<BaseAlert {...props} onClose={onCloseHandle} />, container);
}


type ManageRefObj = {
	add(alert: AlertProps): void;
	remove(key: string): void;
}
const AlertManage = React.forwardRef(function AlertManage(props: {}, ref: Ref<ManageRefObj>) {
	const [list, setList] = useState<{ alert: AlertProps; key: string; in: boolean }[]>([]);
	const queueRef = useRef<{ alert: AlertProps; key: string; in: boolean }[]>([]);
	const add = (alert: AlertProps) => {
		queueRef.current = [...queueRef.current, { alert, key: getUid(), in: true }];
		setList(queueRef.current);
	}
	const remove = (key: string) => {
		queueRef.current = queueRef.current.filter(item => item.key !== key);
		setList(queueRef.current);
	}

	const onCloseHandle = (item: { alert: AlertProps; key: string }) => {
		if (typeof item.alert.onClose === 'function') {
			item.alert.onClose();
		}
		remove(item.key);
	}

	useImperativeHandle(ref, () => {
		return {
			add,
			remove,
		}
	}, []);

	return (
		<TransitionGroup>
			{list.map(item => {
				return (
					<CSSTransition
						in
						key={item.key}
						classNames={{
							appear: 'fade-appear',
							appearActive: 'fade-appear-active',
							appearDone: 'fade-appear-done',
							enter: 'fade-enter',
							enterActive: 'fade-enter-active',
							enterDone: 'fade-enter-done',
							exit: 'fade-exit',
							exitActive: 'fade-exit-active',
							exitDone: 'fade-exit-done'
						}}
						timeout={500}
					>
						<Alert {...item.alert} onClose={() => onCloseHandle(item)} />
					</CSSTransition>
				)
			})}
		</TransitionGroup>
	)
})

const create = (() => {
	const _create: (wrap?: HTMLElement) => Promise<ManageRefObj> = (wrap) => new Promise(function (resolve, reject) {
		function ref(manage: ManageRefObj) {
			resolve(manage);
		}
		ReactDOM.render(
			<AlertManage ref={ref} />,
			getContainer(wrap),
		)
	})
	let cache: [HTMLElement | 'defaultHandle', ManageRefObj] | undefined;
	const list: [HTMLElement | 'defaultHandle', ManageRefObj][] = [];
	return async (wrap?: HTMLElement) => {
		const handle = wrap || 'defaultHandle'
		cache = list.find(item => item[0] === handle);
		if (!cache) {
			cache = [handle, await _create(wrap)]
			list.push(cache);
		}
		return cache[1];
	}
})()

type BasePropsWithContainer = BaseProps & { container?: HTMLElement | null };
const show = async (content: React.ReactNode, option: BaseProps & { container?: HTMLElement }) => {
	const { container, ...otherOption } = option
	const manage = await create(container);
	manage.add({ ...otherOption, children: content });
}

Alert.success = (content: React.ReactNode, option?: Omit<BasePropsWithContainer, 'type'>) => show(content, { type: 'success', ...option });
Alert.warn = (content: React.ReactNode, option?: Omit<BasePropsWithContainer, 'type'>) => show(content, { type: 'warn', ...option });
Alert.error = (content: React.ReactNode, option?: Omit<BasePropsWithContainer, 'type'>) => show(content, { type: 'error', ...option });

export default Alert;
